import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";

import { useRootStore } from "../../../../store/contexts";
import { useDataGridHandlers } from "../../../../common/hooks";
import {
  DataGrid,
  DataGridColumn,
  DataGridRowAction,
} from "../../../../common/controls/lists/DataGrid";
import { SatelliteMachine } from "../../../../api/graphql";
import { SatelliteMachineListStore } from "../../store";
import { userDisplayId } from "../../../user/store/UserCommonStore";
import { satelliteTaskDisplayId } from "../../../satelliteTask/store/SatelliteTaskCommonStore";

import {
  VisibilityIcon,
  EditIcon,
  DeleteForeverIcon,
  ContentCopyIcon,
  CheckIcon,
  BlockIcon,
  PlayArrowIcon,
  DeleteIcon as RevokeIcon,
  PowerSettingsNewIcon as PowerIcon,
  RefreshIcon,
  CloudOffIcon as DisconnectIcon,
} from "../../../../icons";

export type GridExpressProps = { listStore?: SatelliteMachineListStore };

export const SatelliteMachineListForm: React.FC<GridExpressProps> = observer(
  ({ listStore }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    if (!listStore) {
      return null;
    }

    const store = listStore;

    // Handlers de DataGrid (extraídos a hook común)
    const {
      handleColumnVisibilityChange,
      handleColumnOrderChange,
      handleColumnSizingChange,
      handleFiltersChange,
      handleSortingChange,
      handlePaginationChange,
    } = useDataGridHandlers(store, rootStore);

    // Row actions
    const rowActions: DataGridRowAction<SatelliteMachine>[] = useMemo(
      () => [
        {
          label: langStore.viewCommand,
          onClick: (row: SatelliteMachine, event?: React.MouseEvent) =>
            store.viewRow(row.id, event, "menu"),
          icon: <VisibilityIcon fontSize="small" />,
        },
        {
          label: langStore.editCommand,
          onClick: (row: SatelliteMachine, event?: React.MouseEvent) =>
            store.editRow(row.id, event, "menu"),
          icon: <EditIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("update"),
        },
        {
          label: langStore.deleteCommand,
          onClick: (row: SatelliteMachine) => store.deleteRow(row.id),
          icon: <DeleteForeverIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("delete"),
        },
        {
          label: langStore.duplicateCommand,
          onClick: (row: SatelliteMachine, event?: React.MouseEvent) =>
            store.duplicateRow(row.id, event, "menu"),
          icon: <ContentCopyIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("create"),
        },
        {
          label: langStore.satelliteAuthorize,
          onClick: (row: SatelliteMachine) => store.authorizeSatellite(row.id),
          icon: <CheckIcon fontSize="small" color="success" />,
          isVisible: (row: SatelliteMachine) =>
            row.status === "pending_authorization",
        },
        {
          label: langStore.satelliteSuspend,
          onClick: (row: SatelliteMachine) => store.openSuspendDialog(row.id),
          icon: <BlockIcon fontSize="small" color="warning" />,
          isVisible: (row: SatelliteMachine) =>
            row.status === "authorized" || row.status === "online",
        },
        {
          label: langStore.satelliteReactivate,
          onClick: (row: SatelliteMachine) => store.reactivateSatellite(row.id),
          icon: <PlayArrowIcon fontSize="small" color="success" />,
          isVisible: (row: SatelliteMachine) => row.status === "suspended",
        },
        {
          label: langStore.satelliteRevoke,
          onClick: (row: SatelliteMachine) => store.openRevokeDialog(row.id),
          icon: <RevokeIcon fontSize="small" color="error" />,
          isVisible: (row: SatelliteMachine) => row.status !== "revoked",
        },
        {
          label: langStore.satelliteEnable,
          onClick: (row: SatelliteMachine) => store.toggleEnabled(row.id, true),
          icon: <PowerIcon fontSize="small" color="success" />,
          isVisible: (row: SatelliteMachine) => !row.enabled,
        },
        {
          label: langStore.satelliteDisable,
          onClick: (row: SatelliteMachine) =>
            store.toggleEnabled(row.id, false),
          icon: <PowerIcon fontSize="small" color="error" />,
          isVisible: (row: SatelliteMachine) => row.enabled,
        },
        {
          label: langStore.satelliteRotateToken,
          onClick: (row: SatelliteMachine) => store.rotateToken(row.id),
          icon: <RefreshIcon fontSize="small" />,
        },
        {
          label: langStore.satelliteDisconnect,
          onClick: (row: SatelliteMachine) => store.disconnectSatellite(row.id),
          icon: <DisconnectIcon fontSize="small" color="error" />,
          isVisible: (row: SatelliteMachine) => row.status === "online",
        },
      ],
      [langStore, store]
    );

    // Columnas del store con accessorFn para referencias
    const columns = useMemo(() => {
      const storeColumns = store.grid
        .dataGridColumns as unknown as DataGridColumn<SatelliteMachine>[];
      return storeColumns.map((col) => {
        if (col.accessorKey === "certificateExpiresAt") {
          return {
            ...col,
            id: "certificateExpiresAt",
            accessorFn: (row: SatelliteMachine) =>
              row.certificateExpiresAt
                ? new Date(row.certificateExpiresAt).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        if (col.accessorKey === "authTokenCreatedAt") {
          return {
            ...col,
            id: "authTokenCreatedAt",
            accessorFn: (row: SatelliteMachine) =>
              row.authTokenCreatedAt
                ? new Date(row.authTokenCreatedAt).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        if (col.accessorKey === "authTokenRotatedAt") {
          return {
            ...col,
            id: "authTokenRotatedAt",
            accessorFn: (row: SatelliteMachine) =>
              row.authTokenRotatedAt
                ? new Date(row.authTokenRotatedAt).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        if (col.accessorKey === "authorizedAt") {
          return {
            ...col,
            id: "authorizedAt",
            accessorFn: (row: SatelliteMachine) =>
              row.authorizedAt
                ? new Date(row.authorizedAt).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        if (col.accessorKey === "authorizedBy") {
          return {
            ...col,
            id: "authorizedBy",
            accessorFn: (row: SatelliteMachine) =>
              row.authorizedBy
                ? userDisplayId(row.authorizedBy, langStore)
                : "-",
          };
        }
        if (col.accessorKey === "suspendedAt") {
          return {
            ...col,
            id: "suspendedAt",
            accessorFn: (row: SatelliteMachine) =>
              row.suspendedAt
                ? new Date(row.suspendedAt).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        if (col.accessorKey === "suspendedBy") {
          return {
            ...col,
            id: "suspendedBy",
            accessorFn: (row: SatelliteMachine) =>
              row.suspendedBy ? userDisplayId(row.suspendedBy, langStore) : "-",
          };
        }
        if (col.accessorKey === "lastHeartbeatAt") {
          return {
            ...col,
            id: "lastHeartbeatAt",
            accessorFn: (row: SatelliteMachine) =>
              row.lastHeartbeatAt
                ? new Date(row.lastHeartbeatAt).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        if (col.accessorKey === "connectedAt") {
          return {
            ...col,
            id: "connectedAt",
            accessorFn: (row: SatelliteMachine) =>
              row.connectedAt
                ? new Date(row.connectedAt).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        if (col.accessorKey === "disconnectedAt") {
          return {
            ...col,
            id: "disconnectedAt",
            accessorFn: (row: SatelliteMachine) =>
              row.disconnectedAt
                ? new Date(row.disconnectedAt).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        if (col.accessorKey === "tunnelConnectedAt") {
          return {
            ...col,
            id: "tunnelConnectedAt",
            accessorFn: (row: SatelliteMachine) =>
              row.tunnelConnectedAt
                ? new Date(row.tunnelConnectedAt).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        if (col.accessorKey === "tasks") {
          return {
            ...col,
            id: "tasks",
            accessorFn: (row: SatelliteMachine) =>
              row.tasks?.length
                ? row.tasks
                    .map((x: any) => satelliteTaskDisplayId(x, langStore))
                    .join("\n")
                : "-",
          };
        }
        return col;
      });
    }, [store.grid.dataGridColumns, langStore]);

    const rows = store.satelliteMachineCurrentPage || [];

    return (
      <div id={"ListOfSatelliteMachine"}>
        <div id={"StartOfListingOfSatelliteMachine"} />
        <DataGrid
          columns={columns}
          data={rows}
          isLoading={store.status === "fetching"}
          enableFiltering
          enableShowHideFilters
          onFiltersChange={handleFiltersChange}
          initialFilters={store.grid.filtering}
          sorting={store.grid.sorting.map((s) => ({
            id: s.columnName,
            desc: s.direction === "desc",
          }))}
          onSortingChange={handleSortingChange}
          pagination={{
            pageIndex: store.currentPage - 1,
            pageSize: store.pageSize,
          }}
          onPaginationChange={handlePaginationChange}
          rowCount={store.totalRows + (store.hasMorePages ? 1 : 0)}
          manualPagination
          enableColumnOrdering
          enableColumnResizing
          enableHiding
          columnVisibility={store.grid.mrtColumnVisibility}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          columnOrder={store.grid.columnOrder}
          onColumnOrderChange={handleColumnOrderChange}
          columnSizing={store.grid.mrtColumnSizing}
          onColumnSizingChange={handleColumnSizingChange}
          showColumnFilters={store.grid.showColumnFilters}
          onShowColumnFiltersChange={(value) =>
            store.grid.setShowColumnFilters(value)
          }
          isFullScreen={store.grid.isFullScreen}
          onIsFullScreenChange={(value) => store.grid.setIsFullScreen(value)}
          getRowId={(row) => row.id}
          language={langStore.language as "es" | "en" | "pt"}
          onRowClick={(row, event) => store.handleRowClick(row, event)}
          selectedRowId={store.selectedRowId}
          rowActions={rowActions}
          muiTableBodyRowProps={() => ({
            sx: { cursor: "pointer" },
          })}
        />
        <div id={"EndOfListing"} />
      </div>
    );
  }
);
